import PageErrorFallback from '@app/components/layout/page/components/page-error-fallback';
import { ErrorBoundary } from '@sentry/react';
import { useGetSoldPackagingItem } from '@shared/api/hooks/sold-goods/packaging';
import { PackagingActivityRetrieve } from '@shared/api/types';
import { useHistory } from '@shared/providers/history-provider';
import { QueryObserverResult } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

export type TContext = {
  item: PackagingActivityRetrieve;
  status: 'error' | 'success' | 'loading';
  packagingActivityUuid?: string;
  refetchItem: () => Promise<
    QueryObserverResult<PackagingActivityRetrieve, unknown>
  >;
  referrer?: string;
};

const Context = createContext<TContext | null>(null);

export function useSoldPackagingItem() {
  return useContext(Context) as TContext;
}

export default function SoldPackagingItemContext() {
  const params = useParams();
  const packagingActivityUuid = params.item_uuid;
  const history = useHistory();
  const [referrer] = useState<string | undefined>(history.previousLocation);

  const { data, status, refetch } = useGetSoldPackagingItem({
    activityUuid: packagingActivityUuid!,
  });

  const refetchItem = useCallback(() => {
    return refetch();
  }, [refetch]);

  const context: TContext = {
    item: data!,
    status,
    packagingActivityUuid,
    refetchItem,
    referrer,
  };

  return (
    <>
      <ErrorBoundary fallback={<PageErrorFallback />}>
        <Context.Provider value={context}>
          <Outlet />
        </Context.Provider>
      </ErrorBoundary>
    </>
  );
}
